import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import usePrice from "../../hooks/usePrice";
import useFormat from "../../hooks/useFormat";
import {
  fetchUserData,
  addToCarts,
  setCart,
  setUser,
  loadProductData,
  loadCartListDetails
} from "../../redux/actions";
import AddingProductToCartModal from "../../components/modals/AddingProductToCartModal";
import { AnalyticsDataStore } from "../../hooks/analytics-data";

const ProductListCard = ({ product, productId }) => {
  const { productPrice } = usePrice({ product });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formatPrice } = useFormat();
  const url = product?.url?.en;
  const product_url = url.startsWith("/") ? url.slice(1) : url;
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [locationList, setLocationList] = useState([]);
  const [productVariation, setProductVariation] = useState([]);
  const user = useSelector((state) => state?.user_data?.user_data) ?? {};
  const region = useSelector((state) => state.region.region);

  const fetchVariationDetails = useCallback(async () => {
    const data = await loadProductData(product?.slug, region);

    //data for payment summary
    if (data) {
      if (data?.product_variations?.[0]) {
        setProductVariation(data?.product_variations?.[0]);
      }
    }
  }, [product, region]);

  useEffect(() => {
    fetchVariationDetails();
  }, [fetchVariationDetails]);

  const isOutOfStock = () => {
    if (1 > productVariation?.available_stock) {
      return true;
    }
    return productVariation?.available_stock <= 0;
  };

  const addToCart = async () => {
    const toastId = "addToCartError";
    const successToastId = "addToCartSuccess";

    setIsLoading(true);

    if (!user?.locations) {
      let cartId = localStorage.getItem("cartIdKey");

      if (!cartId) {
        cartId = Math.random().toString(36).substring(2, 10);
        localStorage.setItem("cartIdKey", cartId);
      }
      const locationId = cartId;

      try {
        const createCart = await addToCarts(
          productVariation,
          1,
          locationId,
          region,
          dispatch
        );

        if (createCart) {
          dispatch(setCart(createCart));

          //navigate("/cart/one-time");

            const analyticsStore = new AnalyticsDataStore();
            analyticsStore.rootStore = {};
            analyticsStore.add_to_cart(product, productVariation, 'add_to_cart', 1);

          setTimeout(() => {
            if (!toast.isActive(successToastId)) {
              toast.success(
                <div
                  style={{
                    width: "380px",
                    height: "120px",
                  }}
                >
                  <p className="mt-10 mb-20">Product added to cart</p>
                  <div className="d-flex justify-content-between align-items-center gap-3">
                    <button
                      className="btn btn-sm p-10 bg-main-product-50 hover-bg-main-product-50 flex-fill text-center me-2"
                      onClick={() => toast.dismiss(successToastId)}
                    >
                      Shop More
                    </button>

                    <button
                      className="btn btn-sm p-10 bg-main-product-50 hover-bg-main-product-50 flex-fill text-center ms-2"
                      onClick={() => (window.location.href = "/cart/one-time")}
                    >
                      View Cart
                    </button>
                  </div>
                </div>,
                { toastId: successToastId }
              );
            }
          }, 500);
          return;
        }
      } catch (error) {
        console.log(error);
      }
      return;
    } else if (user?.locations?.length === 0) {
      navigate("/self-service/mycompany/actions");
      if (!toast.isActive(toastId)) {
        toast.error(
          "Please add delivery location to add the product in carts.",
          { toastId }
        );
      }
      return;
    } else if (user?.locations?.length === 1) {
      //single location
      const locationId = user?.locations[0]?.id || undefined;

      try {
        const createCart = await addToCarts(
          productVariation,
          1,
          locationId,
          region,
          dispatch
        );

        if (createCart) {
          dispatch(setCart(createCart));

          const user = await fetchUserData();
          if (user) {
            dispatch(setUser(user));

            if(user?.shopping_carts) {
                await loadCartListDetails(user?.shopping_carts, dispatch);
            }

            //navigate("/cart/one-time");

            const analyticsStore = new AnalyticsDataStore();
            analyticsStore.rootStore = {};
            analyticsStore.add_to_cart(product, productVariation, 'add_to_cart', 1);

            setTimeout(() => {
              if (!toast.isActive(successToastId)) {
                toast.success(
                  <div
                    style={{
                      width: "380px",
                      height: "120px",
                    }}
                  >
                    <p className="mt-10 mb-20">Product added to cart</p>
                    <div className="d-flex justify-content-between align-items-center gap-3">
                      <button
                        className="btn btn-sm p-10 bg-main-product-50 hover-bg-main-product-50 flex-fill text-center me-2"
                        onClick={() => toast.dismiss(successToastId)}
                      >
                        Shop More
                      </button>

                      <button
                        className="btn btn-sm p-10 bg-main-product-50 hover-bg-main-product-50 flex-fill text-center ms-2"
                        onClick={() =>
                          (window.location.href = "/cart/one-time")
                        }
                      >
                        View Cart
                      </button>
                    </div>
                  </div>,
                  { toastId: successToastId }
                );
              }
            }, 500);

            return;
          }
        }
      } catch (error) {
        console.log(error);
      }
      return;
    } else if (user?.locations?.length > 1) {
      // multiple location - Select location of the cart
      setLocationList(user?.locations);
      setIsModalOpen(true);
    } else {
      console.warn("noo actions ");
    }

    setIsLoading(true);
  };

  return (
    <div id={productId} className="product-card h-100 p-16 border border-gray-100 hover-border-main-600 rounded-16 position-relative transition-2 ">
      {/* selecting location of the cart */}
      {isModalOpen && (
        <AddingProductToCartModal
          onClose={() => setIsModalOpen(false)}
          locationList={locationList}
          product={product}
          productVariation={productVariation}
          quantity={1}
          region={region}
        />
      )}

      <Link
        to={`/product-details/${encodeURIComponent(product_url)}`}
        className="product-card__thumb flex-center rounded-8 position-relative"
      >
        {product?.jasani_image_url ||
        product?.images?.[0]?.files?.medium?.url ? (
          <img
            src={
              product?.jasani_image_url ??
              product?.images?.[0]?.files?.medium?.url
            }
            alt="product_image"
            className="product_list_image"
          />
        ) : (
          <img
            src="/assets/images/logo/Image-coming-soon.png"
            alt="product_image"
            className="product_list_image"
          />
        )}
      </Link>
      <div className="w-full product-card__content mt-16">
        <Link
          to={`/product-details/${encodeURIComponent(product_url)}`}
          className="link text-line-2"
          tabIndex={0}
        >
          <h6 className="title text-lg fw-semibold mt-12 mb-8">
            {product?.name}
          </h6>
        </Link>
        <div className="flex-align gap-4 text-xs">
          <span className="text-main-600 text-md d-flex">
            <i className="ph-fill ph-storefront" />
          </span>
          <span className="text-gray-500 ">By {product?.brand[0]?.name}</span>

          {product?.brand?.[0]?.name && product?.variation_count ? (
            <span
              className="text-blue "
              style={{ fontSize: "25px", marginBottom: "4px" }}
            >
              {" "}
              •
            </span>
          ) : (
            ""
          )}
          {` ${product?.variation_count} option`}
        </div>
        <Link>
          <div className="product-card__content mt-12">
            <div className="product-card__price mb-16">
              <span className="text-heading text-md fw-semibold">
                {productPrice.salePrice ? (
                  <>
                    <span className="text-small text-medium-electric-blue">
                      {formatPrice(productPrice?.salePrice)}
                    </span>
                    &nbsp;&nbsp;
                    <span className="text-decoration-line-through text-gray-500">
                      {formatPrice(productPrice?.price)}
                    </span>
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-small text-medium-electric-blue">
                    <span>{formatPrice(productPrice?.price)}</span>
                  </div>
                )}
                &nbsp;
                {product?.main_product_attribute?.min_value && (
                  <span className="flex items-center text-small text-grey">
                    {product?.main_product_attribute.min_value}{" "}
                    {product?.main_product_attribute.unit}
                  </span>
                )}
              </span>
            </div>
          </div>
        </Link>

        {product?.variation_count > 1 ? (
          <Link
            to={`/product-details/${encodeURIComponent(product_url)}`}
            className="product-card__cart btn hover-bg-main-600 hover-text-white py-11 px-24 rounded-8 flex-center gap-8"
          >
            More options
          </Link>
        ) : (
          <Link
            className="product-card__cart btn hover-bg-main-600 hover-text-white py-11 px-24 rounded-8 flex-center gap-8"
            onClick={addToCart}
            disabled={
              !productVariation ||
              !productVariation?.base_gross_price ||
              isOutOfStock()
            }
          >
            Add To Cart <i className="ph ph-shopping-cart" />
          </Link>
        )}
      </div>
    </div>
  );
};

export default ProductListCard;
